/**
 * ClickReel SDK Type Definitions
 */

// =============================================================================
// Configuration Types
// =============================================================================

/**
 * Configuration options for initializing ClickReel
 */
/**
 * Floating product action for shopping/product links during video playback
 */
export interface FloatingAction {
  /** Unique identifier for this action */
  id: string;
  /** Display label (e.g., "Black Market Roast") */
  label: string;
  /** Optional subtitle (e.g., price "$19.99") */
  subtitle?: string;
  /** URL to navigate to when clicked */
  url: string;
  /** Optional image URL for product thumbnail */
  imageUrl?: string;
  /** Optional product variants for quick-pick */
  variants?: ProductVariant[];
  /** Query param name to append for selected variant, e.g. "variant" */
  selectedVariantParam?: string;
}

/**
 * Product variant for quick-pick UI on floating actions
 */
export interface ProductVariant {
  id: string;
  label: string;
  type: 'size' | 'color' | 'option';
  colorHex?: string;
  available?: boolean;
}

/**
 * Product card configuration for animated slide-up during video playback
 */
export interface ProductCardConfig {
  enabled: boolean;
  triggerAtPercent?: number;
  productName?: string;
  productPrice?: string;
  productImageUrl?: string;
  ctaText?: string;
  ctaUrl?: string;
  badgeText?: string;
}

/**
 * Social proof overlay configuration
 */
export interface SocialProofConfig {
  enabled: boolean;
  text: string;
  position?: 'top-left' | 'top-right' | 'bottom-left';
  showOnBubble?: boolean;
}

export interface ClickReelConfig {
  /** Unique site key from ClickReel dashboard */
  siteKey: string;
  /** Optional: Override the API endpoint (for testing/staging) */
  apiUrl?: string;
  /** Optional: Enable debug logging */
  debug?: boolean;
  /** Optional: Delay before showing the bubble (ms) */
  delay?: number;
  /** Optional: Position of the bubble */
  position?: BubblePosition;
  /** Optional: Allowed domains for domain whitelist validation */
  allowedDomains?: string[];
  /** Optional: Enable demo mode (skip API fetch, use local config) */
  demoMode?: boolean;
  /** Optional: YouTube video ID for demo mode */
  youtubeVideoId?: string;
  /** Optional: Instagram post/reel URL for demo mode (iframe embed) */
  instagramPostUrl?: string;
  /** Optional: Direct Instagram video MP4 URL for demo mode (full control) */
  instagramVideoUrl?: string;
  /** Optional: TikTok video URL for demo mode */
  tiktokVideoUrl?: string;
  /** Optional: CTA button text for demo mode */
  ctaText?: string;
  /** Optional: CTA button URL for demo mode */
  ctaUrl?: string;
  /** Optional: Override primary color (CTA button, bubble accent) in demo mode */
  primaryColor?: string;
  /** Optional: Request a specific campaign by ID */
  campaignId?: string;
  /** Optional: Override the video ID used in event tracking (for preview with real analytics) */
  videoId?: string;
  /** Optional: Floating product actions shown during video playback */
  floatingActions?: FloatingAction[];
  /** Optional: Direct MP4 URL for animated bubble preview */
  videoPreviewUrl?: string;
  /** Optional: Start video muted with unmute prompt (default: false — plays with sound) */
  muteOnOpen?: boolean;
  /** Optional: Enable multi-video stories UI */
  storyMode?: boolean;
  /** Optional: Product card slide-up during video */
  productCard?: ProductCardConfig;
  /** Optional: Social proof overlay */
  socialProof?: SocialProofConfig;
  /** Optional: Auto-expand/pulse bubble on exit intent */
  exitIntent?: boolean;
  /** Optional: Auto-open video when page scrolled to this % (0-100) */
  scrollAutoOpen?: number;
  /** Optional: Bubble shape (circle, pill, or phone frame) */
  bubbleShape?: 'circle' | 'pill' | 'phone';
  /** Optional: Bubble entrance animation */
  entranceAnimation?: BubbleEntranceAnimation;
  /** Optional: Shake intensity for attention-snap entrance animation */
  shakeIntensity?: ShakeIntensity;
  /** Optional: Bubble size */
  bubbleSize?: 'small' | 'medium' | 'large';
  /** Optional: Preview mode — fetches real API config but skips targeting/dismiss checks */
  previewMode?: boolean;
  /** Optional: Control client-side analytics bridge (fires events to host page's GA4/Meta Pixel) */
  analyticsBridge?: { ga4?: boolean; metaPixel?: boolean };
  /** Optional: Array of video configs for multi-video demo/preview mode */
  videos?: Array<{
    id?: string;
    url: string;
    ctaText?: string;
    ctaUrl?: string;
    /** E2 (2026-04-17): Instagram direct vs embed mode per video */
    playerMode?: 'direct' | 'embed';
  }>;
  /** E2 (2026-04-17): server/preview override for "Powered by ClickReel" watermark */
  showBranding?: boolean;
  /**
   * Sprint 008 (2026-04-29): control remote SDK error reporting.
   * When enabled, internal `logger.error()` calls fire-and-forget POST to
   * the ingest service's `/v1/sdk/errors` endpoint so that field debugging
   * survives the production `console.error` strip done in Sprint 005.
   * Default: enabled. Set `{ enabled: false }` to opt out per-site.
   */
  errorReporting?: { enabled?: boolean };
  /**
   * Sprint 009 (2026-04-29): server-side thumbnail proxy. When `enabled`,
   * `getThumbnailFallbackChain` (called with the matching `proxy` option)
   * prepends a `/v1/thumbnails/{platform}/{id}` URL rooted at `apiUrl` as
   * the FIRST chain entry, ahead of direct-CDN URLs. The proxy serves
   * cached image bytes from the ingest service, eliminating TikTok
   * signed-URL expiry and third-party CDN failures from being visible to
   * end users. Direct-CDN URLs remain tier-2 fallbacks so a proxy outage
   * degrades cleanly to the prior Sprint 001 behaviour. Default: false
   * during rollout; flip to true once verified in production.
   */
  thumbnailProxy?: { enabled: boolean };
}

/**
 * Position options for the bubble
 */
export type BubblePosition = 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left';

/**
 * Campaign configuration from API
 */
export interface CampaignConfig {
  id: string;
  name: string;
  videos: VideoConfig[];
  targeting: TargetingRules;
  styling: StylingConfig;
  trigger: TriggerConfig;
  /** Interactive campaign type (undefined or absent for standard campaigns) */
  type?: 'interactive';
  /** Flow graph for interactive campaigns */
  flow?: CampaignFlowConfig;
}

// =============================================================================
// Interactive Campaign Types
// =============================================================================

export interface CampaignFlowConfig {
  rootNodeId: string | null;
  nodes: InteractionNode[];
  edges: InteractionEdge[];
}

export interface InteractionNode {
  id: string;
  nodeType: 'video' | 'form' | 'call' | 'end_screen';
  label?: string;
  video?: VideoConfig;
  overlay?: OverlayConfig;
  callConfig?: CallNodeConfig;
  sortOrder?: number;
  isRoot?: boolean;
}

export interface CallNodeConfig {
  phoneNumber: string;
  headingText?: string;
  subText?: string;
  buttonText?: string;
}

export type OverlayConfig = OverlayChoicesConfig | FormOverlayConfig;

export interface OverlayChoicesConfig {
  type: 'choices';
  triggerAtPercent?: number;
  triggerOnComplete?: boolean;
  questionText?: string;
  choices: OverlayChoice[];
}

export interface OverlayChoice {
  label: string;
  text: string;
  pointValue?: number;
}

export interface FormOverlayConfig {
  type: 'form';
  triggerAtPercent?: number;
  triggerOnComplete?: boolean;
  headingText?: string;
  fields: FormFieldConfig[];
  submitButtonText?: string;
  submitTargets?: Array<{ integrationId: string; provider: string }>;
  successMessage?: string;
  redirectUrl?: string;
  consentText: string;
  privacyPolicyUrl?: string;
}

export interface FormFieldConfig {
  name: string;
  label: string;
  type: 'text' | 'email' | 'phone' | 'number' | 'select';
  required?: boolean;
  placeholder?: string;
  options?: Array<{ value: string; label: string }>;
}

export interface InteractionEdge {
  id: string;
  sourceNodeId: string;
  targetNodeId: string;
  choiceLabel: string;
  choiceValue?: string;
  pointValue?: number;
  sortOrder?: number;
}

/**
 * Video configuration
 */
export interface VideoConfig {
  id: string;
  url: string;
  thumbnailUrl?: string;
  title?: string;
  duration?: number;
  ctaText?: string;
  ctaUrl?: string;
  orientation: 'portrait' | 'landscape' | 'square';
  /** Optional: YouTube video ID (when using YouTube as source) */
  youtubeId?: string;
  /** Optional: Instagram post/reel URL (for iframe embed mode) */
  instagramPostUrl?: string;
  /** Optional: Direct Instagram video MP4 URL (for full HTML5 control) */
  instagramVideoUrl?: string;
  /** Optional: TikTok video ID (for iframe embed) */
  tiktokId?: string;
  /** Optional: Direct MP4 URL for animated bubble preview */
  videoPreviewUrl?: string;
  /** Optional: Cloudflare Stream video UID */
  cloudflareStreamUid?: string;
  /** Optional: Pre-signed playback token for Cloudflare Stream */
  cloudflareStreamToken?: string;
  /** E2 (2026-04-17): explicit source type (youtube|instagram|tiktok|html5|cloudflare) for DNS-prefetch hinting and player selection */
  videoType?: 'youtube' | 'instagram' | 'tiktok' | 'html5' | 'cloudflare';
  /** E2 (2026-04-17): Instagram direct (MP4) vs embed (iframe) mode */
  playerMode?: 'direct' | 'embed';
  /**
   * Sprint 001 (2026-04-29): Optional ordered list of thumbnail URLs the SDK
   * should try in order before falling back to a branded gradient. Server may
   * emit this for explicit overrides; when absent the SDK computes a chain
   * locally via `getThumbnailFallbackChain(video)`. The last entry MAY be a
   * `gradient:{platform}` marker indicating "show the branded fallback layer".
   */
  thumbnailFallbacks?: string[];
}

/**
 * Targeting rules for campaigns
 */
export interface TargetingRules {
  urls?: string[];
  devices?: ('desktop' | 'mobile' | 'tablet')[];
  geo?: string[];
}

/**
 * Entrance animation for the bubble
 */
export type BubbleEntranceAnimation = 'fade' | 'slide-in' | 'bounce' | 'grow' | 'pulse-in' | 'attention-snap';

/**
 * Morph transition style for player close → bubble
 */
export type MorphStyle = 'portal' | 'slide';

/**
 * Shake intensity for attention-snap entrance
 */
export type ShakeIntensity = 'mild' | 'medium' | 'wild';

/**
 * Configuration for morph visual effects (particles, glow, spring)
 */
export interface MorphEffectsConfig {
  particles?: boolean;
  glow?: boolean;
  springBounce?: boolean;
}

/**
 * Morph target coordinates for transition destination
 */
export interface MorphTarget {
  x: number;
  y: number;
  width: number;
  height: number;
  borderRadius: string;
}

/**
 * Styling configuration
 */
export interface StylingConfig {
  primaryColor?: string;
  position?: BubblePosition;
  bubbleSize?: 'small' | 'medium' | 'large';
  bubbleShape?: 'circle' | 'pill' | 'phone';
  entranceAnimation?: BubbleEntranceAnimation;
  morphStyle?: MorphStyle;
  morphEffects?: MorphEffectsConfig;
  shakeIntensity?: ShakeIntensity;
}

/**
 * Trigger configuration
 */
export interface TriggerConfig {
  type: 'immediate' | 'delay' | 'scroll' | 'exit_intent';
  delaySeconds?: number;
  scrollPercent?: number;
  dismissDays?: number;
  /** Auto-opens video when page scrolled to this % (0-100) */
  scrollOpenAt?: number;
}

// =============================================================================
// Event Types
// =============================================================================

/**
 * Event types emitted by the SDK
 */
export type ClickReelEvent =
  | 'ready'
  | 'bubble:show'
  | 'bubble:hide'
  | 'bubble:click'
  | 'video:open'
  | 'video:close'
  | 'video:play'
  | 'video:pause'
  | 'video:progress'
  | 'video:complete'
  | 'cta:click'
  | 'overlay:show'
  | 'overlay:choice'
  | 'form:show'
  | 'form:submit'
  | 'form:field'
  | 'error';

/**
 * Event data for video progress events
 */
export interface VideoProgressData {
  videoId: string;
  currentTime: number;
  duration: number;
  percent: number;
  quartile: 0 | 25 | 50 | 75 | 100;
}

/**
 * Event handler function type
 */
export type EventHandler<T = unknown> = (data?: T) => void;

/**
 * SDK initialization state
 */
export type SDKState = 'uninitialized' | 'initializing' | 'ready' | 'error';

// =============================================================================
// Constants
// =============================================================================

export const SDK_VERSION = '0.1.0';
export const DEFAULT_API_URL = 'https://api.clickreel.io';
export const Z_INDEX = 2147483647; // Maximum z-index value
export const CONTAINER_ID = 'clickreel-root';

/**
 * SDK settings returned from the init endpoint
 */
export interface SDKSettings {
  showBranding?: boolean;
  planTier?: string;
}
